// Generic confirm modal: title + message + cancel/confirm buttons. Closes on
// backdrop click, Esc, or either button. Use for any "are you sure?" prompt
// where the consequence is non-trivial (sign out, refund, hard delete).

import { useEffect } from 'react'
import { X } from 'lucide-react'
import { Button } from './Button'

export interface ConfirmDialogProps {
  open: boolean
  title: string
  message?: string
  confirmLabel: string
  cancelLabel: string
  /** danger=red confirm button (use for destructive ops). default=primary blue. */
  tone?: 'default' | 'danger'
  onConfirm: () => void
  onCancel: () => void
  /** Disables both buttons (e.g. while the action is pending). */
  busy?: boolean
}

export function ConfirmDialog({
  open, title, message, confirmLabel, cancelLabel,
  tone = 'default', onConfirm, onCancel, busy,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onCancel, busy])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        className="w-full max-w-sm rounded-lg border border-border bg-card p-5 space-y-4 animate-[slideUp_0.18s_ease-out] shadow-lg"
      >
        <header className="flex items-start justify-between gap-3">
          <h2 id="confirm-dialog-title" className="text-base font-semibold tracking-tight pt-0.5">
            {title}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            aria-label={cancelLabel}
          >
            <X size={18}/>
          </button>
        </header>

        {message && (
          <p className="text-sm text-muted-foreground leading-relaxed">{message}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>{cancelLabel}</Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'default'}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
